import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const hasVerified = useRef(false); // Prevent duplicate API calls in React StrictMode

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  useEffect(() => {
    const verifyEmailToken = async () => {
      if (hasVerified.current) {
        console.log('Email verification already attempted, skipping duplicate call');
        return;
      }
      
      hasVerified.current = true;
      console.log('Starting email verification with token:', token, 'and email:', email);
      
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/auth/verify-email?token=${token}&email=${encodeURIComponent(email)}`,
          {
            method: 'GET',
            credentials: 'include'
          }
        );

        const data = await response.json();
        console.log('Verification response:', { ok: response.ok, status: response.status, data });

        if (response.ok && data.success) {
          console.log('Verification successful, setting status to success');
          setStatus('success');
          setMessage(data.message);
          
          // Don't auto-redirect, let user click button
        } else {
          console.log('Verification failed, setting status to error');
          setStatus('error');
          setMessage(data.error || data.message || 'Verification failed');
          setResendEmail(email);
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('Network error occurred during verification');
        setResendEmail(email);
      }
    };

    if (token && email && status === 'verifying') {
      verifyEmailToken();
    } else if (!token || !email) {
      setStatus('error');
      setMessage('Invalid verification link. Missing token or email.');
    }
  }, [token, email, status]);


  const handleResendVerification = async () => {
    if (!resendEmail) return;
    
    setResendLoading(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/resend-verification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ email: resendEmail }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setMessage('New verification email sent! Please check your inbox.');
        setStatus('success');
      } else {
        setMessage(data.error || 'Failed to resend verification email');
      }
    } catch (error) {
      console.error('Resend error:', error);
      setMessage('Network error occurred while resending email');
    }
    
    setResendLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Email Verification
          </h2>
        </div>

        <div className="card p-6">
          {status === 'verifying' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Verifying your email address...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Verification Successful!</h3>
              <p className="text-gray-600 mb-4">{message}</p>
              <p className="text-sm text-gray-500">You can now log in with your account.</p>
              <Link to="/login" className="btn-primary mt-4 inline-block">
                Go to Login Now
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Verification Failed</h3>
              <p className="text-gray-600 mb-4">{message}</p>
              
              {resendEmail && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Didn't receive the email or link expired?
                  </p>
                  <button
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className={`btn-primary ${resendLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                </div>
              )}
              
              <Link to="/register" className="btn-secondary mt-3 inline-block">
                Back to Registration
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;